import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { Settings, Save, ExternalLink, Copy, Check, RefreshCw, CheckCircle, XCircle, AlertCircle, Layout as LayoutIcon, Palette, Eye, Globe, Plus, Trash2, Link as LinkIcon, Mail, Zap, Code, Send, Key, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const WorkspaceSettings = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [workspace, setWorkspace] = useState(null);
  const [settings, setSettings] = useState({
    logo_url: '',
    primary_color: '#7c3aed',
    secondary_color: '#14b8a6',
    custom_domain: '',
    domain_verified: false,
    enable_public_access: true,
    welcome_message: 'Welcome to our Knowledge Base',
    contact_email: '',
    contact_recipients: [],
    enable_email_notifications: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    portal_layout: 'modern',
    portal_theme: 'light',
    show_search: true,
    show_categories_sidebar: true,
    show_ai_chat: true,
    footer_text: '',
    ai_button_text: 'Ask AI',
    enable_language_toggle: true,
    ai_fallback_message: "I couldn't find a specific answer to your question. Would you like to contact our support team?",
    enable_contact_form: true,
    contact_form_title: 'Contact Support',
    contact_form_description: "Fill out this form and we'll get back to you soon.",
    header_buttons: []
  });
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [unlinkingDomain, setUnlinkingDomain] = useState(false);
  
  // Crawl state
  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawlMaxPages, setCrawlMaxPages] = useState(10);
  const [crawling, setCrawling] = useState(false);
  const [crawlResult, setCrawlResult] = useState(null);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [accessKeys, setAccessKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScope, setNewKeyScope] = useState('all');
  const [newKeyCategories, setNewKeyCategories] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchWorkspace();
  }, []);

  const fetchWorkspace = async () => {
    try {
      const res = await axios.get(`${API_URL}/workspaces/my`);
      setWorkspace(res.data);
      if (res.data.settings) {
        const s = res.data.settings;
        setSettings(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(s).map(([k, v]) => [k, v === null ? (prev[k] ?? '') : v])
          )
        }));
      }
      // Fetch access keys and categories
      fetchAccessKeys(res.data.id);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to fetch workspace settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessKeys = async (wsId) => {
    try {
      const id = wsId || workspace?.id;
      if (!id) return;
      const res = await axios.get(`${API_URL}/workspaces/${id}/access-keys`);
      setAccessKeys(res.data.keys || []);
    } catch (error) {
      console.error('Failed to fetch access keys:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) { toast.error('Please enter a key name'); return; }
    setCreatingKey(true);
    try {
      const body = {
        name: newKeyName.trim(),
        scope: newKeyScope,
        categories: newKeyScope === 'categories' ? newKeyCategories.split(',').map(c => c.trim()).filter(Boolean) : [],
        expires_at: newKeyExpiry || null
      };
      await axios.post(`${API_URL}/workspaces/${workspace.id}/access-keys`, body);
      toast.success('Access key created!');
      setNewKeyName('');
      setNewKeyScope('all');
      setNewKeyCategories('');
      setNewKeyExpiry('');
      fetchAccessKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!confirm('Regenerate this key? The old key will stop working immediately.')) return;
    try {
      await axios.post(`${API_URL}/workspaces/${workspace.id}/access-keys/${keyId}/regenerate`);
      toast.success('Key regenerated');
      fetchAccessKeys();
    } catch (error) {
      toast.error('Failed to regenerate key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Revoke this key? Anyone using it will lose access.')) return;
    try {
      await axios.delete(`${API_URL}/workspaces/${workspace.id}/access-keys/${keyId}`);
      toast.success('Key revoked');
      fetchAccessKeys();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  const getKeyUrl = (key) => {
    return `${window.location.origin}/portal/${workspace.subdomain}?key=${key}`;
  };

  const copyKeyUrl = (key) => {
    navigator.clipboard.writeText(getKeyUrl(key));
    toast.success('Link copied to clipboard!');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/workspaces/${workspace.id}/settings`, settings);
      toast.success(t('settingsSaved'));
      fetchWorkspace();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!settings.custom_domain) {
      toast.error('Please enter a domain first');
      return;
    }
    
    setVerifyingDomain(true);
    try {
      const res = await axios.post(`${API_URL}/workspaces/${workspace.id}/verify-domain`, {
        domain: settings.custom_domain
      });
      
      setDomainStatus(res.data);
      
      if (res.data.verified) {
        toast.success('Domain verified successfully!');
        fetchWorkspace();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('Failed to verify domain');
    } finally {
      setVerifyingDomain(false);
    }
  };

  const handleUnlinkDomain = async () => {
    if (!confirm('Are you sure you want to unlink this custom domain?')) return;
    
    setUnlinkingDomain(true);
    try {
      await axios.delete(`${API_URL}/workspaces/${workspace.id}/domain`);
      toast.success('Domain unlinked successfully');
      setSettings(prev => ({ ...prev, custom_domain: '', domain_verified: false }));
      fetchWorkspace();
    } catch (error) {
      toast.error('Failed to unlink domain');
    } finally {
      setUnlinkingDomain(false);
    }
  };

  const handleCrawlWebsite = async () => {
    if (!crawlUrl) {
      toast.error('Please enter a URL to crawl');
      return;
    }
    
    setCrawling(true);
    setCrawlResult(null);
    try {
      const res = await axios.post(`${API_URL}/documents/crawl`, {
        url: crawlUrl,
        max_pages: crawlMaxPages
      });
      setCrawlResult(res.data);
      toast.success(`Successfully crawled ${res.data.documents_created} pages!`);
      setCrawlUrl('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to crawl website');
    } finally {
      setCrawling(false);
    }
  };

  const addHeaderButton = () => {
    setSettings(prev => ({
      ...prev,
      header_buttons: [...(prev.header_buttons || []), { label: '', url: '' }]
    }));
  };

  const removeHeaderButton = (index) => {
    setSettings(prev => ({
      ...prev,
      header_buttons: prev.header_buttons.filter((_, i) => i !== index)
    }));
  };

  const updateHeaderButton = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      header_buttons: prev.header_buttons.map((btn, i) => 
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  const copyEmbedCode = () => {
    const embedCode = `<iframe src="${window.location.origin}/portal/${workspace.subdomain}" width="100%" height="600" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Embed code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSmtpTest = async () => {
    if (!smtpTestEmail) {
      toast.error('Please enter an email address to send the test to');
      return;
    }
    setSmtpTesting(true);
    try {
      const res = await axios.post(`${API_URL}/workspaces/${workspace.id}/smtp-test`, {
        recipient_email: smtpTestEmail
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setSmtpTesting(false);
    }
  };

  const addRecipient = () => {
    const email = newRecipient.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (settings.contact_recipients.includes(email)) {
      toast.error('This email is already in the list');
      return;
    }
    setSettings(prev => ({
      ...prev,
      contact_recipients: [...(prev.contact_recipients || []), email]
    }));
    setNewRecipient('');
  };

  const removeRecipient = (index) => {
    setSettings(prev => ({
      ...prev,
      contact_recipients: prev.contact_recipients.filter((_, i) => i !== index)
    }));
  };

  const publicUrl = workspace ? `${window.location.origin}/portal/${workspace.subdomain}` : '';

  const layoutOptions = [
    { value: 'modern', label: 'Modern', description: 'Clean grid layout with cards' },
    { value: 'classic', label: 'Classic', description: 'Traditional sidebar navigation' },
    { value: 'minimal', label: 'Minimal', description: 'Simple list-based layout' },
    { value: 'card', label: 'Card Grid', description: 'Large card-based gallery' }
  ];

  const themeOptions = [
    { value: 'light', label: 'Light', description: 'Clean white background' },
    { value: 'dark', label: 'Dark', description: 'Dark mode for reduced eye strain' },
    { value: 'auto', label: 'Auto', description: 'Follow system preference' }
  ];

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-violet-600" />
        </div>
      </Layout>
    );
  }

  if (!workspace) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="max-w-4xl mx-auto p-8">
          <p className="text-center text-zinc-600">No workspace found. Please select a workspace first.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-violet-100 flex items-center justify-center">
              <Settings className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('workspaceSettings')}
              </h1>
              <p className="text-sm text-zinc-600">{workspace.name}</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-button">
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('saveSettings')}
          </Button>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-9 lg:w-auto lg:inline-flex">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2" data-testid="settings-tab-team">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Team</span>
            </TabsTrigger>
            <TabsTrigger value="ghl" className="flex items-center gap-2" data-testid="settings-tab-ghl">
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Live Chat</span>
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger value="portal" className="flex items-center gap-2">
              <LayoutIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Portal</span>
            </TabsTrigger>
            <TabsTrigger value="access" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">Access Keys</span>
            </TabsTrigger>
            <TabsTrigger value="domain" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">Domain</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Advanced</span>
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">General Settings</h2>
              <div className="space-y-4">
                <div>
                  <Label>{t('welcomeMessage')}</Label>
                  <Input
                    value={settings.welcome_message}
                    onChange={(e) => setSettings({...settings, welcome_message: e.target.value})}
                    placeholder="Welcome to our Knowledge Base"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{t('contactEmail')}</Label>
                  <Input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                    placeholder="support@company.com"
                    className="mt-2"
                  />
                </div>
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <Label>{t('enablePublicAccess')}</Label>
                    <p className="text-sm text-zinc-600 mt-1">Allow anyone to view your knowledge base</p>
                  </div>
                  <Switch
                    checked={settings.enable_public_access}
                    onCheckedChange={(checked) => setSettings({...settings, enable_public_access: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Language Toggle</Label>
                    <p className="text-sm text-zinc-600 mt-1">Show EN/FR language switcher on portal</p>
                  </div>
                  <Switch
                    checked={settings.enable_language_toggle}
                    onCheckedChange={(checked) => setSettings({...settings, enable_language_toggle: checked})}
                  />
                </div>
              </div>
            </div>

            {/* Public URL & Embed */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Public Access</h2>
              <div className="space-y-4">
                <div>
                  <Label>Public URL</Label>
                  <div className="flex gap-2 mt-2">
                    <Input value={publicUrl} readOnly className="text-sm" />
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('URL copied!'); }}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Embed Code</Label>
                  <Textarea
                    value={`<iframe src="${publicUrl}" width="100%" height="600" frameborder="0"></iframe>`}
                    readOnly
                    rows={3}
                    className="text-xs font-mono mt-2"
                  />
                  <Button variant="outline" size="sm" onClick={copyEmbedCode} className="mt-2">
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy Embed Code'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6" data-testid="team-tab-content">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-zinc-900">Team Management</h2>
                  <p className="text-sm text-zinc-500 mt-1">Invite teammates, manage roles, and reset 2FA for users in this workspace.</p>
                </div>
                <Link to="/users">
                  <Button className="bg-violet-600 hover:bg-violet-700" data-testid="open-team-management">
                    <Users className="h-4 w-4 mr-2" />Open Team Management<ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                <Link to="/users" className="p-4 rounded-xl border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/40 transition-all">
                  <Plus className="h-5 w-5 text-violet-600 mb-2" />
                  <div className="font-semibold text-sm text-zinc-900">Invite users</div>
                  <div className="text-xs text-zinc-500 mt-1">Send invites by email with a chosen role.</div>
                </Link>
                <Link to="/users" className="p-4 rounded-xl border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/40 transition-all">
                  <Settings className="h-5 w-5 text-violet-600 mb-2" />
                  <div className="font-semibold text-sm text-zinc-900">Roles &amp; permissions</div>
                  <div className="text-xs text-zinc-500 mt-1">Set viewer / editor / workspace_admin per user.</div>
                </Link>
                <Link to="/users" className="p-4 rounded-xl border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/40 transition-all">
                  <Key className="h-5 w-5 text-violet-600 mb-2" />
                  <div className="font-semibold text-sm text-zinc-900">Reset 2FA</div>
                  <div className="text-xs text-zinc-500 mt-1">Help users locked out of their authenticator app.</div>
                </Link>
              </div>
            </div>
          </TabsContent>

          {/* GHL Live Chat Tab */}
          <TabsContent value="ghl" className="space-y-6" data-testid="ghl-tab-content">
            <GhlPanel />
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Brand Identity</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <Label>{t('logoUrl')}</Label>
                  <Input
                    value={settings.logo_url}
                    onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                    placeholder="https://example.com/logo.png"
                    className="mt-2"
                  />
                  {settings.logo_url && (
                    <div className="mt-3 p-4 bg-zinc-50 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-2">Preview:</p>
                      <img src={settings.logo_url} alt="Logo preview" className="h-12 object-contain" />
                    </div>
                  )}
                </div>
                <div>
                  <Label>{t('primaryColor')}</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('secondaryColor')}</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Header Buttons */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-zinc-900">Custom Header Buttons</h2>
                  <p className="text-sm text-zinc-600 mt-1">Add navigation links to portal header</p>
                </div>
                <Button variant="outline" size="sm" onClick={addHeaderButton}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Button
                </Button>
              </div>
              <div className="space-y-3">
                {(settings.header_buttons || []).map((btn, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Button label"
                        value={btn.label}
                        onChange={(e) => updateHeaderButton(index, 'label', e.target.value)}
                      />
                      <Input
                        placeholder="https://example.com"
                        value={btn.url}
                        onChange={(e) => updateHeaderButton(index, 'url', e.target.value)}
                      />
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeHeaderButton(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {(!settings.header_buttons || settings.header_buttons.length === 0) && (
                  <p className="text-sm text-zinc-500 text-center py-4">No custom buttons. Click "Add Button" to create one.</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Portal Tab */}
          <TabsContent value="portal" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Portal Layout</h2>
              <div className="grid grid-cols-2 gap-4">
                {layoutOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSettings({...settings, portal_layout: option.value})}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      settings.portal_layout === option.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className="font-semibold text-zinc-900">{option.label}</div>
                    <div className="text-sm text-zinc-600 mt-1">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Theme</h2>
              <div className="grid grid-cols-3 gap-4">
                {themeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSettings({...settings, portal_theme: option.value})}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      settings.portal_theme === option.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className="font-semibold text-zinc-900">{option.label}</div>
                    <div className="text-sm text-zinc-600 mt-1">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Portal Features</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Search Bar</Label>
                    <p className="text-sm text-zinc-600">Show search functionality</p>
                  </div>
                  <Switch checked={settings.show_search} onCheckedChange={(c) => setSettings({...settings, show_search: c})} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Categories Sidebar</Label>
                    <p className="text-sm text-zinc-600">Show category navigation</p>
                  </div>
                  <Switch checked={settings.show_categories_sidebar} onCheckedChange={(c) => setSettings({...settings, show_categories_sidebar: c})} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>AI Chat Widget</Label>
                    <p className="text-sm text-zinc-600">Show Ask AI button</p>
                  </div>
                  <Switch checked={settings.show_ai_chat} onCheckedChange={(c) => setSettings({...settings, show_ai_chat: c})} />
                </div>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">AI Assistant</h2>
              <div className="space-y-4">
                <div>
                  <Label>AI Button Text</Label>
                  <Input
                    value={settings.ai_button_text}
                    onChange={(e) => setSettings({...settings, ai_button_text: e.target.value})}
                    placeholder="Ask AI"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Fallback Message</Label>
                  <Textarea
                    value={settings.ai_fallback_message}
                    onChange={(e) => setSettings({...settings, ai_fallback_message: e.target.value})}
                    rows={2}
                    className="mt-2"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Contact Form Fallback</Label>
                    <p className="text-sm text-zinc-600">Show contact form when AI can't help</p>
                  </div>
                  <Switch checked={settings.enable_contact_form} onCheckedChange={(c) => setSettings({...settings, enable_contact_form: c})} />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Access Keys Tab */}
          <TabsContent value="access" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-2">Private Access Keys</h2>
              <p className="text-sm text-zinc-600 mb-6">Generate shareable links with access keys. Use these to embed your knowledge base in a CRM or share with specific teams — no user account required.</p>

              {/* Create New Key */}
              <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200 mb-6">
                <h3 className="text-sm font-semibold text-zinc-900 mb-3">Create New Key</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <Label>Key Name</Label>
                    <Input
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., CRM Integration, Sales Team"
                      className="mt-1"
                      data-testid="new-key-name"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label>Expires</Label>
                    <Input
                      type="date"
                      value={newKeyExpiry}
                      onChange={(e) => setNewKeyExpiry(e.target.value)}
                      className="mt-1"
                      data-testid="new-key-expiry"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label>Scope</Label>
                    <Select value={newKeyScope} onValueChange={setNewKeyScope}>
                      <SelectTrigger className="mt-1" data-testid="new-key-scope">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Documents</SelectItem>
                        <SelectItem value="categories">Specific Categories</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newKeyScope === 'categories' && (
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Categories (comma separated)</Label>
                      <Input
                        value={newKeyCategories}
                        onChange={(e) => setNewKeyCategories(e.target.value)}
                        placeholder="e.g., Guides, FAQ"
                        className="mt-1"
                        data-testid="new-key-categories"
                      />
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleCreateKey}
                  disabled={creatingKey || !newKeyName.trim()}
                  className="mt-3"
                  data-testid="create-key-button"
                >
                  <Key className="h-4 w-4 mr-2" />
                  {creatingKey ? 'Creating...' : 'Generate Key'}
                </Button>
              </div>

              {/* Keys List */}
              <div className="space-y-3">
                {accessKeys.length === 0 ? (
                  <div className="text-center py-8 text-zinc-400">
                    <Key className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No access keys yet. Create one to generate a shareable link.</p>
                  </div>
                ) : (
                  accessKeys.map((ak) => {
                    const isExpired = ak.expires_at && new Date(ak.expires_at) < new Date();
                    return (
                      <div key={ak.id} className={`p-4 border rounded-lg ${isExpired ? 'bg-red-50 border-red-200' : 'bg-white border-zinc-200'}`} data-testid={`access-key-${ak.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-zinc-900">{ak.name}</span>
                              {isExpired && <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">EXPIRED</span>}
                              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-zinc-100 text-zinc-600 rounded uppercase">{ak.scope === 'categories' ? ak.categories?.join(', ') : 'All'}</span>
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">
                              Created {new Date(ak.created_at).toLocaleDateString()}
                              {ak.expires_at && ` · Expires ${new Date(ak.expires_at).toLocaleDateString()}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => copyKeyUrl(ak.key)} title="Copy link" data-testid={`copy-key-${ak.id}`}>
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleRegenerateKey(ak.id)} title="Regenerate" data-testid={`regenerate-key-${ak.id}`}>
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleRevokeKey(ak.id)} title="Revoke" data-testid={`revoke-key-${ak.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <Input
                            value={getKeyUrl(ak.key)}
                            readOnly
                            className="text-xs font-mono bg-zinc-50 flex-1"
                          />
                          <Button variant="outline" size="sm" onClick={() => copyKeyUrl(ak.key)}>
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </TabsContent>

          {/* Domain Tab */}
          <TabsContent value="domain" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Custom Domain</h2>
              <p className="text-sm text-zinc-600 mb-4">Connect your own domain to the knowledge base</p>
              
              <div className="space-y-4">
                <div>
                  <Label>{t('customDomain')}</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={settings.custom_domain}
                      onChange={(e) => setSettings({...settings, custom_domain: e.target.value})}
                      placeholder="help.yourdomain.com"
                      disabled={settings.domain_verified}
                    />
                    {settings.domain_verified ? (
                      <Button variant="outline" onClick={handleUnlinkDomain} disabled={unlinkingDomain} className="text-red-600">
                        {unlinkingDomain ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Unlink'}
                      </Button>
                    ) : (
                      <Button onClick={handleVerifyDomain} disabled={verifyingDomain || !settings.custom_domain}>
                        {verifyingDomain ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                      </Button>
                    )}
                  </div>
                </div>

                {settings.domain_verified && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">Domain verified and active</span>
                  </div>
                )}

                {domainStatus && !domainStatus.verified && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      <span className="text-sm text-red-700 font-medium">{domainStatus.message}</span>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 mb-2">📋 DNS Setup Instructions:</p>
                  <ol className="text-sm text-blue-600 space-y-1 list-decimal list-inside">
                    <li>Go to your domain's DNS settings</li>
                    <li>Add a CNAME record:</li>
                  </ol>
                  <div className="mt-2 p-2 bg-white rounded font-mono text-xs">
                    <span className="text-zinc-600">Type:</span> CNAME<br/>
                    <span className="text-zinc-600">Name:</span> help (or your subdomain)<br/>
                    <span className="text-zinc-600">Target:</span> <span className="text-blue-600 font-bold">smartdocai-9.preview.emergentagent.com</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Wait 5-10 minutes for DNS propagation, then click Verify.</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email" className="space-y-6">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">SMTP Settings</h2>
              <p className="text-sm text-zinc-600 mb-4">Configure email for contact form notifications</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Host</Label>
                  <Input
                    data-testid="smtp-host-input"
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
                    placeholder="smtp.gmail.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input
                    data-testid="smtp-port-input"
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value) || 587})}
                    placeholder="587"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    data-testid="smtp-username-input"
                    value={settings.smtp_username}
                    onChange={(e) => setSettings({...settings, smtp_username: e.target.value})}
                    placeholder="your-email@gmail.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    data-testid="smtp-password-input"
                    type="password"
                    value={settings.smtp_password}
                    onChange={(e) => setSettings({...settings, smtp_password: e.target.value})}
                    placeholder="App password"
                    className="mt-2"
                  />
                </div>
                <div className="col-span-2">
                  <Label>From Email</Label>
                  <Input
                    data-testid="smtp-from-email-input"
                    value={settings.smtp_from_email}
                    onChange={(e) => setSettings({...settings, smtp_from_email: e.target.value})}
                    placeholder="noreply@yourdomain.com"
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  <AlertCircle className="h-4 w-4 inline-block mr-1" />
                  For Gmail, use an App Password instead of your regular password.
                </p>
              </div>

              {/* SMTP Test */}
              <div className="mt-6 pt-4 border-t border-zinc-200">
                <h3 className="text-sm font-semibold text-zinc-900 mb-3">Test SMTP Connection</h3>
                <div className="flex gap-2">
                  <Input
                    data-testid="smtp-test-email-input"
                    value={smtpTestEmail}
                    onChange={(e) => setSmtpTestEmail(e.target.value)}
                    placeholder="Send test email to..."
                    type="email"
                    className="flex-1"
                  />
                  <Button
                    data-testid="smtp-test-button"
                    variant="outline"
                    onClick={handleSmtpTest}
                    disabled={smtpTesting || !smtpTestEmail}
                  >
                    {smtpTesting ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {smtpTesting ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">Save your SMTP settings first, then send a test email to verify the connection.</p>
              </div>
            </div>

            {/* Contact Form Recipients */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-2">Contact Form Recipients</h2>
              <p className="text-sm text-zinc-600 mb-4">When someone submits the contact form, notification emails will be sent to these addresses.</p>
              
              <div className="flex gap-2 mb-4">
                <Input
                  data-testid="new-recipient-input"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                  placeholder="team@company.com"
                  type="email"
                  className="flex-1"
                />
                <Button data-testid="add-recipient-button" variant="outline" onClick={addRecipient}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>

              <div className="space-y-2">
                {(settings.contact_recipients || []).map((email, index) => (
                  <div key={index} data-testid={`recipient-item-${index}`} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-zinc-400" />
                      <span className="text-sm text-zinc-700">{email}</span>
                    </div>
                    <Button
                      data-testid={`remove-recipient-${index}`}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => removeRecipient(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {(!settings.contact_recipients || settings.contact_recipients.length === 0) && (
                  <div className="text-center py-6 text-zinc-400">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No recipients configured. Add email addresses to receive contact form notifications.</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-6">
            {/* Website Crawler */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-2">
                <Globe className="h-5 w-5 inline-block mr-2" />
                Import from Website
              </h2>
              <p className="text-sm text-zinc-600 mb-4">Crawl a website and import its content as documents</p>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label>Website URL</Label>
                    <Input
                      value={crawlUrl}
                      onChange={(e) => setCrawlUrl(e.target.value)}
                      placeholder="https://docs.example.com"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Max Pages</Label>
                    <Input
                      type="number"
                      value={crawlMaxPages}
                      onChange={(e) => setCrawlMaxPages(parseInt(e.target.value) || 10)}
                      min={1}
                      max={50}
                      className="mt-2"
                    />
                  </div>
                </div>
                <Button onClick={handleCrawlWebsite} disabled={crawling || !crawlUrl} className="w-full">
                  {crawling ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Crawling...</>
                  ) : (
                    <><Globe className="h-4 w-4 mr-2" />Start Crawling</>
                  )}
                </Button>
                {crawlResult && (
                  <div className={`p-4 rounded-lg ${crawlResult.documents_created > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <p className="font-medium text-sm">
                      {crawlResult.documents_created > 0 ? <CheckCircle className="h-4 w-4 inline-block mr-2 text-green-600" /> : <AlertCircle className="h-4 w-4 inline-block mr-2 text-yellow-600" />}
                      Crawled {crawlResult.pages_crawled} pages, created {crawlResult.documents_created} documents
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h2 className="text-lg font-bold text-zinc-900 mb-4">Footer</h2>
              <div>
                <Label>Footer Text</Label>
                <Textarea
                  value={settings.footer_text}
                  onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                  placeholder="© 2025 Your Company. All rights reserved."
                  rows={2}
                  className="mt-2"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default WorkspaceSettings;

// =================== GHL Live Chat Panel ===================
function GhlPanel() {
  const [config, setConfig] = React.useState({ enabled: false, location_id: '', pit_token: '', default_channel: 'Live_Chat' });
  const [existing, setExisting] = React.useState(null);
  const [testing, setTesting] = React.useState(false);
  const [saving2, setSaving2] = React.useState(false);

  React.useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/ghl/config`);
      setExisting(r.data);
      if (r.data?.configured) {
        setConfig({
          enabled: r.data.enabled,
          location_id: r.data.location_id || '',
          pit_token: '',
          default_channel: r.data.default_channel || 'Live_Chat',
        });
      }
    } catch (e) { /* not configured yet */ }
  };

  const testConn = async () => {
    if (!config.location_id || !config.pit_token) { toast.error('Enter both Location ID and PIT'); return; }
    setTesting(true);
    try {
      const r = await axios.post(`${API_URL}/ghl/test-connection`, { location_id: config.location_id, pit_token: config.pit_token });
      if (r.data.ok) toast.success(`Connected to "${r.data.location_name}"`);
      else toast.error(`Failed (${r.data.status_code || '?'}): ${r.data.error || 'check token + location id'}`);
    } catch (e) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  const saveConfig = async () => {
    if (!config.location_id || !config.pit_token) { toast.error('Location ID and PIT are both required'); return; }
    setSaving2(true);
    try {
      const r = await axios.post(`${API_URL}/ghl/save-config`, config);
      toast.success('GHL connected');
      setExisting({ configured: true, ...config, webhook_url_path: r.data.webhook_url_path });
      setConfig(c => ({ ...c, pit_token: '' }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving2(false); }
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const webhookFullUrl = existing?.webhook_url_path ? `${origin}${existing.webhook_url_path}` : '';

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <Zap className="h-5 w-5 text-violet-600" />Live Chat — GoHighLevel
          </h2>
          <p className="text-sm text-zinc-500 mt-1">When a visitor clicks &quot;Send us a message&quot; in the chatbot, a conversation is created in your GHL account. Agent replies appear in the widget.</p>
        </div>
        {existing?.configured && (
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
            <CheckCircle className="h-3.5 w-3.5" />Connected
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-medium">GHL Sub-Account Location ID</Label>
            <Input
              value={config.location_id}
              onChange={e => setConfig({ ...config, location_id: e.target.value })}
              placeholder="e.g. ADVlSQnPsdq3hinusd6C3"
              className="mt-1.5"
              data-testid="ghl-location-id-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Default Channel</Label>
            <select
              value={config.default_channel}
              onChange={e => setConfig({ ...config, default_channel: e.target.value })}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-zinc-200 bg-white text-sm"
              data-testid="ghl-channel-select"
            >
              <option value="Live_Chat">Live Chat</option>
              <option value="SMS">SMS</option>
              <option value="Custom">Custom</option>
            </select>
          </div>
        </div>
        <div>
          <Label className="text-sm font-medium">Private Integration Token (PIT)</Label>
          <Input
            type="password"
            value={config.pit_token}
            onChange={e => setConfig({ ...config, pit_token: e.target.value })}
            placeholder={existing?.configured ? `Saved: ${existing.pit_token_masked || '••••'} — leave blank to keep` : 'Paste your GHL PIT here'}
            className="mt-1.5"
            data-testid="ghl-pit-input"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Generate in GHL → Settings → Private Integrations. Required scopes: <code className="text-xs bg-zinc-100 px-1 rounded">contacts.write</code>, <code className="text-xs bg-zinc-100 px-1 rounded">conversations.write</code>, <code className="text-xs bg-zinc-100 px-1 rounded">conversations/message.write</code>, <code className="text-xs bg-zinc-100 px-1 rounded">locations.readonly</code>.
          </p>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200">
          <div>
            <div className="text-sm font-semibold">Enable Live Chat with Human</div>
            <div className="text-xs text-zinc-500">Show &quot;Send us a message&quot; in the chatbot Messages tab</div>
          </div>
          <Switch checked={!!config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} data-testid="ghl-enabled-toggle" />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={testConn} disabled={testing} data-testid="ghl-test-button">
            {testing ? 'Testing…' : 'Test Connection'}
          </Button>
          <Button onClick={saveConfig} disabled={saving2} className="bg-violet-600 hover:bg-violet-700" data-testid="ghl-save-button">
            <Save className="h-4 w-4 mr-2" />{saving2 ? 'Saving…' : 'Save & Connect'}
          </Button>
        </div>

        {existing?.configured && webhookFullUrl && (
          <div className="pt-4 mt-2 border-t border-zinc-100">
            <h3 className="font-bold text-sm text-zinc-900 mb-1">Receive Agent Replies (Webhook)</h3>
            <p className="text-xs text-zinc-500 mb-2">In GHL, create a workflow with trigger &quot;Customer Replied&quot; (or &quot;New Chat Message&quot;) and add a Webhook action with the URL below.</p>
            <code className="block w-full px-3 py-2 bg-zinc-900 text-zinc-100 rounded-lg text-xs font-mono overflow-x-auto break-all">{webhookFullUrl}</code>
            <p className="text-xs text-zinc-500 mt-2">Include these fields in the workflow custom-data: <code className="text-xs bg-zinc-100 px-1 rounded">contactId</code>, <code className="text-xs bg-zinc-100 px-1 rounded">message</code>, <code className="text-xs bg-zinc-100 px-1 rounded">direction</code>, <code className="text-xs bg-zinc-100 px-1 rounded">locationId</code>.</p>
          </div>
        )}
      </div>
    </div>
  );
}
